// components/calculator/ResultsSection.js
import React from 'react';
import {
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';

// Definir todos los meses disponibles con sus emojis
const MESES_INFO = {
  ene: { nombre: '🗓️ Enero', emoji: '❄️' },
  feb: { nombre: '🗓️ Febrero', emoji: '💝' },
  mar: { nombre: '🗓️ Marzo', emoji: '🌸' },
  abr: { nombre: '🗓️ Abril', emoji: '🌷' },
  may: { nombre: '🗓️ Mayo', emoji: '🌻' },
  jun: { nombre: '🗓️ Junio', emoji: '☀️' },
  jul: { nombre: '🗓️ Julio', emoji: '🏖️' },
  ago: { nombre: '🗓️ Agosto', emoji: '🌴' },
  sep: { nombre: '🗓️ Septiembre', emoji: '🍂' },
  oct: { nombre: '🗓️ Octubre', emoji: '🎃' },
  nov: { nombre: '🗓️ Noviembre', emoji: '🦃' },
  dic: { nombre: '🗓️ Diciembre', emoji: '🎄' }
};

const ResultsSection = ({ calculatedData, showIVA, setShowIVA, IVA_RATE }) => {
  // Validación para evitar errores cuando calculatedData está incompleto
  if (!calculatedData || !calculatedData.montoBase) {
    return null;
  }

  // Función helper para formatear moneda
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0,00';
    }
    return amount.toLocaleString('es-AR', { minimumFractionDigits: 2 });
  };

  // Función helper para formatear porcentaje
  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined || isNaN(percentage)) {
      return '0,00';
    }
    return percentage.toFixed(2);
  };

  // Generar dinámicamente los meses basado en los datos calculados
  const generateMonthsData = () => {
    const months = [];
    
    // Si tenemos mesesOrdenados en calculatedData, usarlos
    if (calculatedData.mesesOrdenados && Array.isArray(calculatedData.mesesOrdenados)) {
      calculatedData.mesesOrdenados.forEach(mes => {
        const mesKey = mes.key;
        const mesInfo = MESES_INFO[mesKey];
        
        if (mesInfo) {
          const montoKey = `monto${mesKey.charAt(0).toUpperCase() + mesKey.slice(1)}`;
          const porcentajeAcumKey = `porcentajeAcum${mesKey.charAt(0).toUpperCase() + mesKey.slice(1)}`;
          
          months.push({
            key: mesKey,
            nombre: `${mesInfo.emoji} ${mesInfo.nombre}`,
            monto: calculatedData[montoKey] || 0,
            porcentaje: calculatedData[porcentajeAcumKey] || 0
          });
        }
      });
    } else {
      // Fallback: buscar meses que tengan datos (para compatibilidad con versión anterior)
      const mesesConDatos = [];
      
      Object.keys(MESES_INFO).forEach(mesKey => {
        const montoKey = `monto${mesKey.charAt(0).toUpperCase() + mesKey.slice(1)}`;
        const porcentajeAcumKey = `porcentajeAcum${mesKey.charAt(0).toUpperCase() + mesKey.slice(1)}`;
        
        if (calculatedData[montoKey] && calculatedData[montoKey] > 0) {
          mesesConDatos.push({
            key: mesKey,
            order: Object.keys(MESES_INFO).indexOf(mesKey) + 1,
            nombre: `${MESES_INFO[mesKey].emoji} ${MESES_INFO[mesKey].nombre}`,
            monto: calculatedData[montoKey] || 0,
            porcentaje: calculatedData[porcentajeAcumKey] || 0
          });
        }
      });
      
      // Ordenar por orden cronológico
      months.push(...mesesConDatos.sort((a, b) => a.order - b.order));
    }
    
    return months;
  };

  const months = generateMonthsData();

  // Si no hay meses para mostrar, mostrar mensaje
  if (months.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          📊 Resultados por Mes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No hay datos de meses para mostrar. Asegúrese de seleccionar meses y realizar el cálculo.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        📊 Resultados por Mes ({months.length} {months.length === 1 ? 'mes' : 'meses'})
      </Typography>
      
      <FormControlLabel
        control={
          <Checkbox
            checked={showIVA}
            onChange={(e) => setShowIVA(e.target.checked)}
            color="primary"
          />
        }
        label="Mostrar montos con IVA (21%)"
        sx={{ mb: 2 }}
      />

      <Grid container spacing={2}>
        {months.map((mes, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={mes.key || index}>
            <Card sx={{ 
              height: '100%',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ 
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'primary.main'
                }}>
                  {mes.nombre}
                </Typography>
                
                <Typography variant="h5" color="primary" sx={{ 
                  fontWeight: 'bold',
                  mb: 1
                }}>
                  ${formatCurrency(mes.monto)}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  +{formatPercentage(mes.porcentaje)}% acumulado
                </Typography>
                
                <Typography variant="caption" color="text.disabled">
                  (Sin IVA)
                </Typography>
                
                {showIVA && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="h6" color="secondary" sx={{ 
                      fontWeight: 'bold'
                    }}>
                      ${formatCurrency(mes.monto * (1 + IVA_RATE))}
                    </Typography>
                    <Typography variant="body2" color="secondary">
                      Con IVA (21%)
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Información adicional */}
      <Paper elevation={1} sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary">
          💡 <strong>Información:</strong> Los porcentajes mostrados son acumulativos desde el monto base. 
          {months.length > 1 && ` El último mes (${months[months.length - 1].nombre.replace(/🗓️|❄️|💝|🌸|🌷|🌻|☀️|🏖️|🌴|🍂|🎃|🦃|🎄/g, '').trim()}) representa el aumento total.`}
        </Typography>
      </Paper>
    </Paper>
  );
};

export default ResultsSection;